import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  OnInit,
  viewChild,
} from '@angular/core';
import { environment } from '@environments/environment';
import { GlobalService } from '@pages/global/services';
import { CacheService } from '@services/cache';
import { MiddleOfficeService } from '@services/middle-office';
import { ButtonComponent } from '@shared/components/button';
import { middleOfficeButtonConfig } from '@shared/components/middle-office-button/config';
import { MiddleOfficeButtonType } from '@shared/components/middle-office-button/enum';
import { MiddleOfficeButton } from '@shared/components/middle-office-button/model';
import { IsStoreUuidPipe } from '@shared/components/middle-office-button/pipe';
import { SafeUrlPipe } from '@shared/pipes';
import { combineLatest, filter, from, map, Observable, switchMap } from 'rxjs';

@Component({
  selector: 'app-middle-office-button',
  imports: [ButtonComponent, SafeUrlPipe, AsyncPipe, IsStoreUuidPipe],
  templateUrl: './middle-office-button.component.html',
  styleUrl: './middle-office-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MiddleOfficeButtonComponent implements OnInit {
  readonly type = input.required<keyof typeof MiddleOfficeButtonType>();
  config!: MiddleOfficeButton;
  apiUrl$!: Observable<string>;
  allowedStoresUuids$: Observable<string[]>;
  currentStoreUuid$!: Observable<string | null>;
  readonly dialogRef = viewChild.required<ElementRef<HTMLDialogElement>>('dialogRef');
  readonly #middleOfficeService = inject(MiddleOfficeService);
  readonly #cacheService = inject(CacheService);
  readonly #globalService = inject(GlobalService);

  constructor() {
    this.currentStoreUuid$ = this.#middleOfficeService.currentStoreUuid$;
    this.allowedStoresUuids$ = from(this.#cacheService.get('middleOfficeToken') as Promise<string>).pipe(
      switchMap((token) => this.#middleOfficeService.getStores$(token, true)),
      map((stores) => stores.stores?.map((store) => store.uuid))
    );
  }

  @HostListener('window:message', ['$event.data']) onMessageEvent(data: { event: string }) {
    if (data.event !== 'closeIframeCallback') return;
    this.#globalService.closeIframeCallbackEvent$.next();
    this.onClose();
  }

  ngOnInit() {
    this.config = middleOfficeButtonConfig[MiddleOfficeButtonType[this.type()]]!;
    this.apiUrl$ = combineLatest([
      this.currentStoreUuid$,
      from(this.#cacheService.get('middleOfficeToken') as Promise<string>),
    ]).pipe(
      filter(([storeUuid, token]) => !!storeUuid && !!token),
      map(
        ([storeUuid, token]) =>
          `${environment.API.endpoint.default}/iframe/${storeUuid ? storeUuid : ''}${this.config.endpoint}?token=${token}`
      )
    );
  }

  onClose() {
    this.dialogRef().nativeElement.close();
  }

  onClick() {
    this.dialogRef().nativeElement.showModal();
  }
}
